import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Send, ArrowLeft, Sparkles } from 'lucide-react';
import { MessageTicks } from './MessageTicks';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTimestamp = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch (error) {
    return value;
  }
};

export const MessageThread = () => {
  useEffect(() => { document.title = 'Messages - SIYAK'; }, []);
  const { threadId } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const fetchThread = useCallback(async () => {
    if (!threadId || !user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/messages/threads/${threadId}`, {
        withCredentials: true,
      });
      setThread(response.data?.thread || null);
      setMessages(response.data?.messages || []);

      await axios.put(`${API_URL}/api/messages/threads/${threadId}/read`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('Thread not found');
      } else if (status === 403) {
        toast.error('You do not have access to this thread');
      } else {
        toast.error('Failed to load thread');
      }
    } finally {
      setLoading(false);
    }
  }, [threadId, user?.id]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [messages]
  );

  const handleSuggestReply = async () => {
    setSuggesting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads/${threadId}/suggest-reply`,
        {},
        { withCredentials: true }
      );
      setBody(response.data.suggestion || '');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not generate a suggestion');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || !threadId) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/messages/threads/${threadId}`,
        { body: trimmed },
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, response.data]);
      setBody('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-4" data-testid="message-thread-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Button variant="ghost" asChild className="-ml-2">
              <Link to="/messages">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to threads
              </Link>
            </Button>
            <h1 className="font-heading text-2xl font-bold text-navy-900 mt-1">
              {thread?.counterpart?.name || 'Conversation'}
            </h1>
            <p className="text-sm text-muted-foreground">{thread?.property?.title || 'Property thread'}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Messages</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 max-h-[55vh] overflow-y-auto">
            {sortedMessages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No messages yet.</p>
            ) : (
              sortedMessages.map((message) => {
                const isOwn = message.sender_id === user?.id;
                return (
                  <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-3 ${isOwn ? 'bg-royal-600 text-white' : 'bg-muted'}`}>
                      <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                      <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <p className={`text-[11px] ${isOwn ? 'text-royal-100' : 'text-muted-foreground'}`}>
                          {formatTimestamp(message.created_at)}
                        </p>
                        {isOwn && <MessageTicks isRead={message.is_read} />}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <form onSubmit={handleSend} className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={4}
            maxLength={5000}
          />
          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSuggestReply}
              disabled={suggesting || sending}
            >
              {suggesting
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Suggest reply
            </Button>
            <Button type="submit" disabled={sending || body.trim().length === 0}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send message
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};
